import * as React from 'react';

import { Card, Dialog, InputGroup } from '@blueprintjs/core';
import CardContainer from '../common/CardContainer';
import UserDetails from '../../models/UserDetails';
import './ProfilePage.scss';
import { Login } from '../../models/forms/Login';
import AnimateHeight from 'react-animate-height';
import { Form } from 'react-final-form';
import FormTextbox from '../controls/forms/FormTextbox';
import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import actions from 'src/actions';
import { connect } from 'react-redux';

interface IProfilePageStateProps {
    user: UserDetails;
    confirmPasswordDialogShowing: boolean;
    setNewPasswordDialogShowing: boolean;
    enableTwoFactorDialogShowing: boolean;
    confirmFailed: boolean;
    passwordMatchFailed: boolean;
    qrImageCode: string;
    manualEntryKey: string;
    passwordErrors: string[];
}

export interface IProfilePageDispatchProps {
    showConfirmPasswordDialog: () => void;
    confirmPassword: (payload: { login: Login; dialogName: string }) => void;
    confirmTwoFactorCode: (payload: { twoFactorAuthPin: string }, persistSession: boolean) => void;
    hideDialog: () => void;
    setNewPassword: (password: string) => void;
    passwordsDoNotMatch: () => void;
}

type IProfilePageProps = IProfilePageStateProps & IProfilePageDispatchProps;
class ProfilePage extends React.Component<IProfilePageProps> {
    nextDialog = '';

    hideDialog = () => {
        this.props.hideDialog();
    };

    showConfirmPasswordDialog = (dialog: string) => {
        this.nextDialog = dialog;
        this.props.showConfirmPasswordDialog();
    };

    confirmPassword = (dialog: string) => {
        const confpassword = document.getElementById('currentPassword') as HTMLInputElement;
        if (confpassword.value !== null && confpassword.value !== '') {
            const login = new Login();
            login.username = this.props.user.email;
            login.password = confpassword.value;

            const options = {
                login,
                dialogName: dialog,
            };

            this.props.confirmPassword(options);
        } else {
            (document.getElementById('currentPasswordLabel') as HTMLInputElement).innerHTML = 'ENTER A PASSWORD!';
            (document.getElementById('currentPassword') as HTMLInputElement).style.backgroundColor = 'LightYellow';
        }
    };

    setNewPassword = () => {
        // validate
        const newPassword = (document.getElementById('newPassword') as HTMLInputElement).value;
        const rePassword = (document.getElementById('reEnteredPassword') as HTMLInputElement).value;
        if (newPassword !== rePassword || rePassword == null || rePassword === '') {
            this.passwordsDoNotMatch();
        } else {
            this.props.setNewPassword(rePassword);
        }
    };

    passwordsDoNotMatch = () => {
        this.props.passwordsDoNotMatch();
    };

    public render() {
        return (
            <>
                <CardContainer height="400px">
                    <Card elevation={1} className="bp3-card.bp3-dark">
                        <div className="card-header">User Profile Page</div>
                        <div className="bp3-label">Username: {this.props.user ? this.props.user.username : null}</div>
                        <div className="bp3-label">User Email: {this.props.user ? this.props.user.email : null}</div>
                        <a onClick={() => this.showConfirmPasswordDialog('setNewPassword')}>Reset Password</a>

                        <div className="card-header">Two Factor Authentication</div>
                        <div>Currently {this.props.user.twoFactorEnabled ? 'Enabled' : 'Disabled'}</div>
                        <a
                            onClick={
                                this.props.user.twoFactorEnabled
                                    ? () => this.showConfirmPasswordDialog('disableTwoFactor')
                                    : () => this.showConfirmPasswordDialog('enableTwoFactor')
                            }
                        >
                            {this.props.user.twoFactorEnabled ? 'Disable' : 'Enable'} Two Factor
                        </a>
                    </Card>
                </CardContainer>

                <Dialog
                    title="Confirm Password"
                    isOpen={this.props.confirmPasswordDialogShowing}
                    onClose={this.hideDialog}
                    className="password-dialog"
                >
                    <div>
                        <AnimateHeight
                            duration={200}
                            height={this.props.confirmFailed ? 'auto' : 0}
                            contentClassName="lh-error-panel lh-mb-small lh-align-center"
                        >
                            Incorrect Password
                        </AnimateHeight>
                        <label className="password-label" id="currentPasswordLabel">
                            Enter Current Password
                        </label>
                        <input className="bp3-input test" type="password" id="currentPassword" />
                    </div>
                    <div>
                        <button className="bp3-button" onClick={() => this.confirmPassword(this.nextDialog)}>
                            Confirm
                        </button>
                    </div>
                </Dialog>

                <Dialog
                    title="Enable Two Factor Authentication"
                    isOpen={this.props.enableTwoFactorDialogShowing}
                    onClose={this.hideDialog}
                    className="password-dialog"
                >
                    <div className="scan-code">
                        <h3>Scan Code Below</h3>
                        <img className="qr-code" src={this.props.qrImageCode} />
                        <h3>Enter Code</h3>
                        <Form
                            onSubmit={(payload: { twoFactorAuthPin: string }) => {
                                const persistSession = window.localStorage.getItem('jwt') ? true : false;

                                this.props.confirmTwoFactorCode(payload, persistSession);
                            }}
                            render={({ handleSubmit, pristine, invalid }) => {
                                return (
                                    <form onSubmit={handleSubmit}>
                                        <FormTextbox name="twoFactorAuthPin" label="Auth Code" placeholder="Enter Two Factor Auth Code" />
                                        <button type="submit" disabled={pristine || invalid}>
                                            Submit Code
                                        </button>{' '}
                                    </form>
                                );
                            }}
                        />
                    </div>
                </Dialog>

                <Dialog
                    title="Set New Password"
                    isOpen={this.props.setNewPasswordDialogShowing}
                    onClose={this.hideDialog}
                    className="password-dialog"
                >
                    <div>
                        <AnimateHeight
                            duration={200}
                            height={this.props.passwordMatchFailed || this.props.passwordErrors.length > 0 ? 'auto' : 0}
                            contentClassName="lh-error-panel lh-mb-small lh-align-center"
                        >
                            {this.props.passwordMatchFailed ? 'Passwords Do Not Match' : ''}
                            {this.props.passwordErrors.length > 0 ? (
                                <ul>
                                    {this.props.passwordErrors.map((error, index) => (
                                        <div key={`error-${index}`}>{error}</div>
                                    ))}
                                </ul>
                            ) : (
                                ''
                            )}
                        </AnimateHeight>
                        <div className="split-dialog-left">
                            <label className="bp3-label">
                                Enter New Password
                                <InputGroup leftIcon="lock" type="password" id="newPassword" />
                            </label>
                        </div>
                        <div className="split-dialog-right">
                            <label className="bp3-label">
                                Re-Enter New Password
                                <InputGroup leftIcon="lock" type="password" id="reEnteredPassword" />
                            </label>
                        </div>
                    </div>
                    <div>
                        <button className="bp3-button" onClick={this.setNewPassword}>
                            Save
                        </button>
                    </div>
                </Dialog>
            </>
        );
    }
}

const mapStateToProps = (state: IRootState) => ({
    user: selectors.auth.user(state),
    confirmPasswordDialogShowing: selectors.layout.isDialogShowing(state, 'confirmPassword'),
    setNewPasswordDialogShowing: selectors.layout.isDialogShowing(state, 'setNewPassword'),
    enableTwoFactorDialogShowing: selectors.layout.isDialogShowing(state, 'enableTwoFactor'),
    confirmFailed: selectors.profile.confirmFailed(state),
    passwordMatchFailed: selectors.profile.passwordMatchFailed(state),
    qrImageCode: selectors.profile.qrImageCodeUrl(state),
    manualEntryKey: selectors.profile.manualEntryKey(state),
    passwordErrors: state.profile.passwordErrors,
});

const mapDispatchToProps: IProfilePageDispatchProps = {
    showConfirmPasswordDialog: () => actions.layout.showDialog('confirmPassword'),
    hideDialog: () => actions.layout.hideDialog(),
    confirmPassword: (options: { login: Login; dialogName: string }) => actions.profile.confirm(options),
    confirmTwoFactorCode: (payload: { twoFactorAuthPin: string }, persistSession: boolean) =>
        actions.profile.confirmTwoFactor({ twoFactorAuthPin: payload.twoFactorAuthPin, persistSession }),
    setNewPassword: (password: string) => actions.profile.setNewPassword(password),
    passwordsDoNotMatch: () => actions.profile.passwordsDoNotMatch(),
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfilePage);



// WEBPACK FOOTER //
// ./src/components/profile/ProfilePage.tsx